import { useCallback, useState } from "react";
import { PromptLibrary } from "@/types/models";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, ToggleLeft, ToggleRight, Search, Trash2, History } from "lucide-react";
import { PromptLibraryFormDialog } from "@/components/templates/PromptLibraryFormDialog";
import { usePromptLibraries, usePromptLibraryAdmin, usePromptLibraryUsage } from "@/hooks/usePromptLibrary";
import { useAdminTemplateTypes, useAdminTemplateFormatTypes } from "@/hooks/useMasterData";
import { useFilterSort } from "@/hooks/useFilterSort";
import { usePromptUsageCount } from "@/hooks/useProjects";
import { projectTypeVariant } from "@/config/projectTypeConfig";
import { v4 as uuidv4 } from "uuid";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog";
import { AdminAuditHistoryDialog } from "@/components/admin/AdminAuditHistoryDialog";

const promptSortComparators = {
  prompt: (a: PromptLibrary, b: PromptLibrary, _templateTypeMap: Map<number, string>, _formatTypeMap: Map<number, string>) => (a.priming ?? "").localeCompare(b.priming ?? ""),
};

export default function PromptTable() {
  const { data: primings = [], isLoading } = usePromptLibraries();
  const { data: templateTypes = [] } = useAdminTemplateTypes();
  const { data: formatTypes = [] } = useAdminTemplateFormatTypes();
  const { data: usageData = [], isLoading: isUsageLoading } = usePromptLibraryUsage();
  const { create, update, remove } = usePromptLibraryAdmin();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PromptLibrary | null>(null);
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [deletingPrompt, setDeletingPrompt] = useState<PromptLibrary | null>(null);
  const [historyPrompt, setHistoryPrompt] = useState<PromptLibrary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { data: promptProjectUsage } = usePromptUsageCount(deletingPrompt?.id);
  const selectedPromptUsage = selectedPromptId
    ? usageData.find((u) => u.prompt_library_id === selectedPromptId) ?? null
    : null;

  const getSearchableText = useCallback(
    (p: PromptLibrary) => [p.name, p.priming ?? ""],
    []
  );
  const getTemplateTypeId = useCallback((p: PromptLibrary) => p.template_type_id, []);
  const getFormatTypeIds = useCallback((p: PromptLibrary) => p.template_formatetype_ids, []);
  const getIsActive = useCallback((p: PromptLibrary) => p.is_active, []);

  const {
    cycleSort,
    getSortIcon,
    searchText,
    setSearchText,
    filterTemplateType,
    setFilterTemplateType,
    filterFormatType,
    setFilterFormatType,
    filterStatus,
    setFilterStatus,
    sortedItems: sorted,
    templateTypeMap,
    formatTypeMap,
  } = useFilterSort<PromptLibrary>({
    items: primings,
    templateTypes,
    formatTypes,
    getSearchableText,
    getTemplateTypeId,
    getFormatTypeIds,
    getIsActive,
    sortComparators: promptSortComparators,
    defaultSort: (a, b) => a.name.localeCompare(b.name),
  });

  const handleCreate = async (values: Partial<PromptLibrary>) => {
    await create.mutateAsync({ id: uuidv4(), ...values });
    setDialogOpen(false);
  };

  const handleUpdate = async (values: Partial<PromptLibrary>) => {
    if (!editing) return;
    await update.mutateAsync({ id: editing.id, data: values });
    setEditing(null);
    setDialogOpen(false);
  };

  const handleToggleActive = (priming: PromptLibrary) => {
    update.mutate({ id: priming.id, data: { is_active: !priming.is_active } });
  };

  const openEditDialog = (priming: PromptLibrary) => {
    setEditing(priming);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPrompt) return;
    setIsDeleting(true);
    try {
      await remove.mutateAsync(deletingPrompt.id);
    } finally {
      setIsDeleting(false);
      setDeletingPrompt(null);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Filter Block */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search prompts..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={filterTemplateType} onValueChange={setFilterTemplateType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Template Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Template Types</SelectItem>
              {templateTypes.map((tt) => (
                <SelectItem key={tt.id} value={String(tt.id)}>{tt.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterFormatType} onValueChange={setFilterFormatType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Format Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Format Types</SelectItem>
              {formatTypes.map((ft) => (
                <SelectItem key={ft.id} value={String(ft.id)}>{ft.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreateDialog} size="sm" className="bg-brand-teal">
            <Plus className="w-4 h-4 mr-2" />
            Add Prompt
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No prompts found. Click "Add Prompt" to create one.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => cycleSort("name")}>
                  <div className="flex items-center gap-1">
                    Name
                    {getSortIcon("name")}
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => cycleSort("templateType")}>
                  <div className="flex items-center gap-1">
                    Template Type
                    {getSortIcon("templateType")}
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => cycleSort("formatTypes")}>
                  <div className="flex items-center gap-1">
                    Format Types
                    {getSortIcon("formatTypes")}
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => cycleSort("prompt")}>
                  <div className="flex items-center gap-1">
                    Prompt
                    {getSortIcon("prompt")}
                  </div>
                </TableHead>
                <TableHead className="w-[100px] cursor-pointer select-none" onClick={() => cycleSort("status")}>
                  <div className="flex items-center gap-1">
                    Status
                    {getSortIcon("status")}
                  </div>
                </TableHead>
                <TableHead className="w-[100px]">Usage</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((priming) => (
                <TableRow key={priming.id}>
                  <TableCell className="font-medium">{priming.name}</TableCell>
                  <TableCell>
                    {(() => {
                      const typeName = templateTypeMap.get(priming.template_type_id ?? -1);
                      return typeName ? (
                        <Badge variant={projectTypeVariant[typeName.toUpperCase()] ?? "secondary"}>{typeName}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {priming.template_formatetype_ids?.length
                      ? priming.template_formatetype_ids
                          .map(id => formatTypeMap.get(id))
                          .filter(Boolean)
                          .join(", ")
                      : "-"}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-xs">
                    {priming.priming ? (
                      <span className="block truncate" title={priming.priming}>
                        {priming.priming}
                      </span>
                    ) : (
                      <span className="italic">—</span>
                    )}
                  </TableCell>          
                  <TableCell>
                    <Badge variant={priming.is_active ? "default" : "secondary"}>
                      {priming.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      if (isUsageLoading) {
                        return <span className="text-muted-foreground">—</span>;
                      }
                      const usage = usageData.find((u) => u.prompt_library_id === priming.id);
                      const count = usage?.template_count ?? 0;
                      if (count > 0) {
                        return (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 h-auto font-medium"
                            onClick={() => setSelectedPromptId(priming.id)}
                          >
                            {count}
                          </Button>
                        );
                      }
                      return <span className="text-muted-foreground">0</span>;
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(priming)}
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleActive(priming)}
                        title={priming.is_active ? "Deactivate" : "Activate"}
                      >
                        {priming.is_active ? (
                          <ToggleRight className="w-4 h-4" />
                        ) : (
                          <ToggleLeft className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setHistoryPrompt(priming)}
                        title="View History"
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeletingPrompt(priming)}
                        title="Delete"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <PromptLibraryFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        defaultValues={editing ?? undefined}
        onSubmit={editing ? handleUpdate : handleCreate}
        isSubmitting={create.isPending || update.isPending}
      />

      <AdminAuditHistoryDialog
        open={!!historyPrompt}
        onClose={() => setHistoryPrompt(null)}
        source="prompt"
        entityId={historyPrompt?.id}
        entityName={historyPrompt?.name}
        entityLabel="Prompt"
      />

      <DeleteConfirmationDialog
        open={!!deletingPrompt}
        title="Delete Prompt"
        description="This will permanently delete this prompt. This action cannot be undone."
        itemName={deletingPrompt?.name}
        warningText={
          promptProjectUsage && promptProjectUsage.usage_count > 0
            ? `This prompt is used by ${promptProjectUsage.usage_count} project(s). Deleting it will not affect those projects' own copies.`
            : undefined
        }
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingPrompt(null)}
      />

      <Dialog open={!!selectedPromptId} onOpenChange={(open) => { if (!open) setSelectedPromptId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Templates Using This Prompt</DialogTitle>
          </DialogHeader>
          <ul className="space-y-2 mt-2">
            {selectedPromptUsage?.templates.map((t) => (
              <li key={t.id} className="text-sm">{t.name}</li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
